/**
 * returnLedgerDataSource.ts — WatermelonDB bridge for the return ledger.
 *
 * Loads how much of an invoice has already come back, so the return screen and
 * the credit-note writer can both cap against it. See `returnLedger.ts` for the
 * rule; this file only fetches.
 */

import { Q } from '@nozbe/watermelondb';
import type { Database } from '@nozbe/watermelondb';
import { Invoice, LineItem, VOIDED_INVOICE_STATUSES } from '@/db/models/Billing';
import { remainingReturnable, type ReturnedLine, type SoldLine } from './returnLedger';

/**
 * Quantities already returned against `originalInvoiceId`, by product.
 *
 * A voided credit note is a return that was itself cancelled, so the goods are
 * back on the customer's side of the counter and returnable again — hence the
 * status filter.
 */
export async function loadReturnedQuantities(
    db: Database,
    originalInvoiceId: string,
): Promise<ReturnedLine[]> {
    const creditNotes = await db.get<Invoice>('invoices').query(
        Q.where('reference_invoice_id', originalInvoiceId),
        Q.where('type', 'CREDIT_NOTE'),
        Q.where('is_deleted', false),
        Q.where('status', Q.notIn(VOIDED_INVOICE_STATUSES)),
    ).fetch();

    if (creditNotes.length === 0) return [];

    const returned: ReturnedLine[] = [];
    const ids = creditNotes.map((cn) => cn.id);
    // SQLite caps `IN (?, ?, …)` at 999 parameters; batch well under it.
    for (let i = 0; i < ids.length; i += 500) {
        const lines = await db.get<LineItem>('line_items').query(
            Q.where('invoice_id', Q.oneOf(ids.slice(i, i + 500))),
            Q.where('is_deleted', false),
        ).fetch();
        for (const l of lines) {
            returned.push({ productId: l.productId, quantity: l.quantity, description: l.description });
        }
    }
    return returned;
}

/**
 * Remaining returnable quantity per line of `originalInvoiceId`, keyed by line
 * item id. The one call both the screen and the writer make.
 */
export async function loadRemainingReturnable(
    db: Database,
    originalInvoiceId: string,
): Promise<Record<string, number>> {
    const soldRows = await db.get<LineItem>('line_items').query(
        Q.where('invoice_id', originalInvoiceId),
        Q.where('is_deleted', false),
    ).fetch();

    const sold: SoldLine[] = soldRows.map((l) => ({
        id: l.id,
        productId: l.productId,
        quantity: l.quantity,
        description: l.description,
    }));

    return remainingReturnable(sold, await loadReturnedQuantities(db, originalInvoiceId));
}
